// Master Intake Schema — BAS/NAS/TAS
// Each section: {key, title, hint?, fields: [{key, label, type, options?, placeholder?, max?, conditional?}]}
// Types: text | textarea | number | slider | single | multi | multi3 | checkbox | file | date

export const MASTER_INTAKE_SCHEMA = [
  {
    key: 'intro', title: 'Decode Your Terrain. Not Just Your Diagnosis.',
    hint: "This assessment maps your Biological Arrival State (BAS), Neurological Arrival State (NAS), and Terrain & Toxic Arrival State (TAS). It is not a medical diagnosis.",
    fields: [],
  },
  {
    key: 'basics', title: 'Basics',
    fields: [
      { key: 'email', label: 'Email', type: 'text', required: true },
      { key: 'first_name', label: 'First Name', type: 'text' },
      { key: 'last_name', label: 'Last Name', type: 'text' },
      { key: 'contact_number', label: 'Contact Number', type: 'text', required: true },
      { key: 'city', label: 'City', type: 'text' },
      { key: 'gender', label: 'Gender', type: 'single', options: ['Male', 'Female'] },
      { key: 'age', label: 'Age', type: 'number' },
      { key: 'height_cm', label: 'Height (cm)', type: 'number' },
      { key: 'weight_kg', label: 'Weight (kg)', type: 'number' },
      { key: 'preferred_language', label: 'Preferred Language for Support', type: 'multi',
        options: ['Gujarati', 'Hindi', 'English', 'Other'] },
    ],
  },
  {
    key: 'life_family_context', title: 'Life & Family Context',
    fields: [
      { key: 'marital_status', label: 'Marital Status', type: 'single',
        options: ['Single', 'Married', 'Divorced', 'Widow / Widower'] },
      { key: 'profession', label: 'Profession / Role', type: 'textarea' },
      { key: 'is_caregiver', label: 'Are you a parent or primary caregiver?', type: 'single',
        options: ['Yes', 'No'] },
      { key: 'spiritual_orientation', label: 'Spiritual orientation (if any)', type: 'text' },
      { key: 'religious_timing', label: 'Religious / spiritual timing constraints', type: 'text' },
    ],
  },
  {
    key: 'sleep_rhythm', title: 'Sleep, Rhythm & Daily Life',
    fields: [
      { key: 'wake_time', label: 'Average wake-up time', type: 'text', placeholder: 'e.g. 6:30 AM' },
      { key: 'sleep_time', label: 'Average sleep time', type: 'text', placeholder: 'e.g. 11:00 PM' },
      { key: 'sleep_quality', label: 'Sleep quality (0–10)', type: 'slider', max: 10 },
      { key: 'sleep_trouble', label: 'Trouble falling asleep or waking at night', type: 'single',
        options: ['Yes', 'No'] },
      { key: 'sleep_trouble_detail', label: 'If yes, please comment', type: 'textarea',
        conditional: { field: 'sleep_trouble', value: 'Yes' } },
      { key: 'alarm_used', label: 'Do you use an alarm to wake up?', type: 'single', options: ['Yes', 'No'] },
      { key: 'naps', label: 'Do you nap during the day?', type: 'single',
        options: ['No', 'Yes – short (<30 min)', 'Yes – long (>30 min)'] },
      { key: 'screens_after_10pm', label: 'Do you use screens after 10 PM?', type: 'single',
        options: ['Yes', 'No'] },
    ],
  },
  {
    key: 'movement', title: 'Movement & Physical Activity',
    fields: [
      { key: 'primary_activity', label: 'Primary physical activity', type: 'text',
        placeholder: 'e.g. walking, yoga, gym' },
      { key: 'activity_frequency', label: 'Frequency (days per week)', type: 'number' },
      { key: 'sitting_hours', label: 'Hours of sitting per day', type: 'number' },
      { key: 'physical_limitations', label: 'Any physical limitations or injuries?', type: 'textarea' },
      { key: 'pain_stiffness', label: 'Muscle pain, stiffness, or spasms?', type: 'textarea' },
    ],
  },
  {
    key: 'diet', title: 'Dietary Structure & Hydration',
    fields: [
      { key: 'diet_type', label: 'Type of diet', type: 'single',
        options: ['Pure Veg', 'Jain', 'No Onion No Garlic', 'Vegan', 'Eggitarian', 'Non-veg'] },
      { key: 'cooking_methods', label: 'Cooking methods commonly used', type: 'multi',
        options: ['Fresh', 'Packaged', 'Microwave', 'Oily / fried'] },
      { key: 'cravings', label: 'Cravings', type: 'multi',
        options: ['Sugar', 'Salt', 'Caffeine', 'None', 'Other'] },
      { key: 'fruits_veg_daily', label: 'Daily fruits & vegetables intake', type: 'single',
        options: ['0 servings', '1–2', '3–4', '5+'] },
      { key: 'fermented_foods', label: 'Use of fermented foods (curd, kefir, kanji, etc.)',
        type: 'single', options: ['Yes', 'No'] },
      { key: 'water_intake', label: 'Daily water intake', type: 'single',
        options: ['<1 L', '1 L', '2 L', '3 L+'] },
      { key: 'water_source', label: 'Water source', type: 'single',
        options: ['RO', 'Filtered', 'Tap', 'Borewell', 'Bottled', 'Copper pot', 'Other'] },
      { key: 'salt_type', label: 'Salt type used', type: 'single',
        options: ['Refined', 'Rock', 'Himalayan', 'Mixed', 'Not sure'] },
      { key: 'meal_skipping', label: 'Do you skip meals or fast?', type: 'single',
        options: ['Yes', 'No', 'Other'] },
    ],
  },
  {
    key: 'medications', title: 'Medications & Therapies (History)',
    fields: [
      { key: 'current_medications', label: 'Current medications (if any)', type: 'textarea' },
      { key: 'past_longterm_meds', label: 'Past long-term medications', type: 'textarea' },
      { key: 'med_types', label: 'Medication types used long-term', type: 'multi',
        options: ['Steroids', 'Antibiotics', 'Painkillers', 'Hormonal meds', 'Other'] },
      { key: 'supplements', label: 'Supplements currently taken', type: 'textarea' },
      { key: 'ayurvedic_homeopathic', label: 'Any Ayurvedic / Homeopathic treatment?',
        type: 'single', options: ['Yes', 'No', 'Other'] },
      { key: 'iv_cleansing_history', label: 'Any IV therapy / ozone / chelation / panchkarma in past?',
        type: 'single', options: ['Yes', 'No', 'Other'] },
      { key: 'major_surgery', label: 'Any major surgery in past?', type: 'textarea' },
    ],
  },
  {
    key: 'pattern_trigger', title: 'Pattern & Trigger Intelligence',
    hint: 'What clusters of symptoms or triggers do you notice?',
    fields: [
      { key: 'pattern_clusters', label: 'Which of these usually happen together in your body?',
        type: 'multi', options: [
          'Constipation / delayed bowel movement + body heaviness',
          'Poor sleep + more pain / stiffness / fatigue',
          'Stress / emotional upset + symptom worsening',
          'Delayed meals + weakness / irritability / uneasiness',
          'Overexertion + next-day crash / heaviness',
          'Medication delay + anxiety / heaviness / symptom increase',
          'Noise / light / crowd + internal discomfort',
          'Travel / irregular routine + symptom instability',
          'Certain foods + bloating / heaviness / discomfort',
          'Emotional support + noticeable symptom relief',
          'Rest / sleep + noticeable symptom relief',
          'None clear yet', 'Other',
        ] },
      { key: 'worsening_triggers', label: 'What most commonly worsens your symptoms? (select up to 3)',
        type: 'multi3', options: [
          'Poor sleep', 'Stress / worry', 'Delayed meals', 'Constipation / poor bowel movement',
          'Certain foods', 'Overexertion', 'Screen time / overstimulation',
          'Travel / routine disruption', 'Medication delay', 'Heat / cold',
          'Hormonal changes', 'Social / family conflict', 'No clear trigger', 'Other',
        ] },
      { key: 'improving_factors', label: 'What most commonly improves your symptoms? (select up to 3)',
        type: 'multi3', options: [
          'Good sleep', 'Rest', 'Bowel movement', 'Hydration', 'Food / meal on time',
          'Medication', 'Emotional calm / reassurance', 'Nature / vacation / peaceful environment',
          'Exercise / movement', 'Prayer / meditation / spiritual grounding',
          'Nothing helps clearly', 'Other',
        ] },
      { key: 'bad_day_first_sign', label: "When your body has a bad day, what usually happens first?",
        type: 'textarea', placeholder: 'The first sign you notice before the rest of the symptoms build up.' },
    ],
  },
  {
    key: 'bas', title: 'BAS™ Biological Arrival State',
    fields: [
      { key: 'morning_energy', label: 'Morning energy on waking (0–10)', type: 'slider', max: 10 },
      { key: 'energy_pattern', label: 'Energy pattern during the day', type: 'single',
        options: ['Stable', 'Drops after noon', 'Peaks only evening', 'Always low', 'Unpredictable'] },
      { key: 'recovery_time', label: 'Recovery time after exertion', type: 'single',
        options: ['Same day', '24 hours', '2–3 days', '3+ days'] },
      { key: 'morning_laziness', label: 'Morning laziness / no desire to wake (0–10)', type: 'slider', max: 10 },
      { key: 'morning_stiffness', label: 'Morning stiffness duration', type: 'single',
        options: ['None', '<15 minutes', '15–60 minutes', '60+ minutes'] },
      { key: 'heat_cold_tolerance', label: 'Heat / cold tolerance', type: 'single',
        options: ['Comfortable in both', 'Cold intolerant', 'Heat intolerant', 'Both intolerant'] },
      { key: 'exercise_feel', label: 'During exercise you feel', type: 'single',
        options: ['Strong & stable', 'Weak early', 'Breathless quickly', 'Dizzy / unwell'] },
      { key: 'appetite_pattern', label: 'Appetite pattern', type: 'single',
        options: ['Normal', 'Low', 'Erratic', 'Constant hunger', 'No hunger cues'] },
      { key: 'post_meal_feel', label: 'How do you feel 30–60 minutes after meals?', type: 'single',
        options: ['Energized', 'Neutral', 'Heavy / sleepy', 'Anxious / uncomfortable'] },
      { key: 'bloating', label: 'Bloating/heaviness after meals (0–10)', type: 'slider', max: 10 },
      { key: 'stool_frequency', label: 'Stool frequency', type: 'single',
        options: ['1/day', '2/day', '3+/day'] },
      { key: 'stool_consistency', label: 'Stool consistency', type: 'single',
        options: ['Loose', 'Normal', 'Hard', 'Alternating', 'Urgency'] },
      { key: 'laxative_use', label: 'Do you take laxatives for bowel movement (Ayurvedic churn, Harade, etc.)',
        type: 'single', options: ['Yes - Daily', 'Yes - Occasionally', 'No', 'Other'] },
      { key: 'food_tolerance', label: 'Food tolerance', type: 'single', options: ['Good', 'Limited', 'Poor'] },
      { key: 'supplement_tolerance', label: 'Supplement tolerance', type: 'single',
        options: ['Good', 'Limited', 'Poor', 'Not tried'] },
      { key: 'acidity', label: 'Acidity / reflux', type: 'single',
        options: ['None', 'Mild', 'Severe', 'After meals'] },
      { key: 'weight_trend', label: 'Weight trend last 12 months', type: 'single',
        options: ['Stable', 'Gradual gain', 'Gradual loss', 'Fluctuating'] },
      { key: 'wound_healing', label: 'Healing speed of small wounds', type: 'single',
        options: ['Normal', 'Slow', 'Very slow'] },
      { key: 'sickness_frequency', label: 'Frequency of falling sick', type: 'single',
        options: ['Rare', 'Occasional', 'Frequent'] },
      { key: 'gum_oral_sensitivity', label: 'Gum pain / oral sensitivity', type: 'single',
        options: ['Yes', 'No', 'Other'] },
      { key: 'cold_hands_feet', label: 'Cold hands / feet', type: 'single',
        options: ['No', 'Sometimes', 'Often'] },
    ],
  },
  {
    key: 'nas', title: 'NAS™ Neurological Arrival State',
    fields: [
      { key: 'internal_calm', label: 'Sense of internal calm (0–10)', type: 'slider', max: 10 },
      { key: 'noise_light_sensitivity', label: 'Sensitivity to noise / light', type: 'single',
        options: ['Normal', 'Heightened', 'Severe'] },
      { key: 'breathing_feel', label: 'Your natural breathing feels', type: 'single',
        options: ['Deep & slow', 'Shallow', 'Irregular', 'Frequently sighing / yawning'] },
      { key: 'relax_without_effort', label: 'Ability to relax without effort', type: 'single',
        options: ['Yes', 'No', 'Other'] },
      { key: 'palpitations', label: 'Palpitations / fast heartbeat', type: 'single',
        options: ['No', 'Rare', 'Weekly', 'Frequent'] },
      { key: 'stress_recovery_time', label: 'After stress, how long to feel normal again?', type: 'single',
        options: ['Minutes', 'Hours', 'Full day', 'Several days'] },
      { key: 'feels_safe_in_body', label: 'Do you generally feel safe and settled inside your body?',
        type: 'single', options: ['Yes', 'Sometimes', 'Rarely', 'Almost never'] },
      { key: 'decision_making', label: 'Making decisions feels', type: 'single',
        options: ['Easy', 'Slightly tiring', 'Very tiring', 'Avoided whenever possible'] },
      { key: 'sleep_latency', label: 'Time taken to fall asleep', type: 'single',
        options: ['<20 min', '20–60 min', '60+ min'] },
      { key: 'night_awakenings', label: 'Night awakenings', type: 'single',
        options: ['None', '1–2', 'Frequent', 'Wake too early'] },
      { key: 'waking_feel', label: 'Feeling on waking', type: 'single',
        options: ['Refreshed', 'Heavy', 'Exhausted', 'Anxious'] },
      { key: 'pain_pattern', label: 'Pain pattern', type: 'single',
        options: ['Fixed', 'Migratory', 'Whole body', 'One side dominant'] },
      { key: 'pain_disproportionate', label: 'Pain disproportionate to activity', type: 'single',
        options: ['Yes', 'No', 'Other'] },
      { key: 'touch_sensitivity', label: 'Sensitivity to touch/pressure', type: 'single',
        options: ['Yes', 'No', 'Other'] },
      { key: 'eye_strain', label: 'Eye heaviness / strain / eye feels smaller (0–10)', type: 'slider', max: 10 },
      { key: 'brain_fog', label: 'Brain fog', type: 'single', options: ['None', 'Occasional', 'Daily'] },
      { key: 'emotional_state', label: 'Emotional state', type: 'single',
        options: ['Stable', 'Irritable', 'Anxious', 'Low drive / shutdown', 'Mood swings'] },
    ],
  },
  {
    key: 'tas', title: 'TAS™ Terrain & Toxic Arrival State',
    fields: [
      { key: 'occupational_chemicals', label: 'Occupational chemical exposure', type: 'single',
        options: ['Yes', 'No', 'Other'] },
      { key: 'live_near_traffic', label: 'Living near heavy traffic / agriculture / industry',
        type: 'single', options: ['Yes', 'No', 'Other'] },
      { key: 'mold_exposure', label: 'Past mold or damp exposure', type: 'single',
        options: ['Yes', 'No', 'Other'] },
      { key: 'smell_triggers', label: 'Do smells/chemicals trigger symptoms?', type: 'single',
        options: ['No', 'Mild (perfumes, cleaners)', 'Moderate', 'Severe'] },
      { key: 'antibiotic_history', label: 'History of repeated antibiotic courses', type: 'single',
        options: ['Rare', 'Occasional', 'Frequent', 'Long courses / multiple years'] },
      { key: 'past_detox_response', label: 'Past detox / cleansing made you feel', type: 'single',
        options: ['Better', 'No change', 'Worse', 'Never tried'] },
      { key: 'longterm_meds_detail', label: 'Long-term medication use (>1 year)', type: 'textarea' },
      { key: 'metal_fillings_implants', label: 'Metal fillings / implants / tooth extractions', type: 'textarea' },
      { key: 'skin_issues', label: 'Skin issues', type: 'multi',
        options: ['Rashes', 'Itching', 'Pigmentation', 'Acne', 'Dryness', 'None'] },
      { key: 'unclear_headaches', label: 'Headaches without clear cause', type: 'single',
        options: ['Yes', 'No', 'Other'] },
      { key: 'unclear_joint_muscle_pain', label: 'Joint or muscle pain without injury', type: 'single',
        options: ['Yes', 'No', 'Other'] },
      { key: 'internal_heaviness', label: 'Internal heaviness / toxic feeling (0–10)', type: 'slider', max: 10 },
      { key: 'sweating', label: 'Sweating pattern', type: 'single',
        options: ['Normal', 'Excessive', 'Very low'] },
      { key: 'urine_changes', label: 'Urine changes', type: 'single',
        options: ['Normal', 'Dark', 'Very light', 'Variable with odor'] },
      { key: 'odor_change', label: 'Body/breath odor change', type: 'single',
        options: ['Yes', 'No', 'Other'] },
      { key: 'diet_resistant_fat', label: 'Fat gain resistant to diet', type: 'single',
        options: ['Yes', 'No', 'Other'] },
      { key: 'flare_without_trigger', label: 'Symptom flares without trigger', type: 'single',
        options: ['Yes', 'No', 'Other'] },
      { key: 'worsen_after_stress', label: 'Symptoms worsen after stress/exertion', type: 'single',
        options: ['Yes', 'No', 'Other'] },
    ],
  },
  {
    key: 'bnt_intersection', title: 'BAS–NAS–TAS Intersection',
    fields: [
      { key: 'improve_in_nature', label: 'Symptom improvement during vacations / nature', type: 'single',
        options: ['Improve significantly', 'Improve slightly', 'No change', 'Worsen', 'Other'] },
      { key: 'improve_with_emotional_support', label: 'Symptom improvement when emotionally supported',
        type: 'single', options: ['Improve', 'Stay same', 'Worsen', 'Other'] },
      { key: 'internal_reserve', label: 'Your sense of internal reserve', type: 'single',
        options: ['Strong', 'Moderate', 'Low', 'Almost depleted', 'Other'] },
      { key: 'overdo_recovery_time', label: 'If you overdo activity, how long to recover?', type: 'multi',
        options: ['Same day', 'By next day', '2–3 days', 'Several days', 'Very unpredictable', 'Not sure'] },
      { key: 'fastest_relief', label: 'When your body feels worse, what helps fastest?', type: 'multi',
        options: ['Sleep', 'Rest', 'Food', 'Bowel movement', 'Hydration', 'Medicine',
                  'Emotional reassurance / calm support', 'Exercise / movement',
                  'Prayer / meditation / silence', 'Nothing helps clearly', 'Other'] },
      { key: 'improve_in_calm_life', label: 'Symptoms improve when life becomes calmer?', type: 'single',
        options: ['Yes, strongly', 'Yes, slightly', 'No major change', 'Sometimes', 'Not sure'] },
      { key: 'body_behaves_best_when', label: 'In what kind of situation does your body behave best?',
        type: 'textarea' },
    ],
  },
  {
    key: 'reproductive', title: 'Reproductive & Hormonal History',
    fields: [
      { key: 'cycle_regularity', label: 'Menstrual cycle regularity (if applicable)', type: 'single',
        options: ['Regular', 'Irregular', 'Post-menopause'] },
      { key: 'pcos_fibroids_endo', label: 'PCOS / Fibroids / Endometriosis', type: 'textarea' },
      { key: 'pregnancy_history', label: 'Pregnancy history', type: 'textarea' },
      { key: 'libido', label: 'Libido', type: 'single', options: ['Low', 'Normal', 'High'] },
      { key: 'hormonal_mood_swings', label: 'Hormonal mood swings', type: 'single',
        options: ['Yes', 'No', 'Other'] },
    ],
  },
  {
    key: 'family_epigenetic', title: 'Family & Epigenetic History',
    fields: [
      { key: 'family_conditions', label: 'Family history of:', type: 'multi', options: [
        'Diabetes', 'High blood pressure', 'Thyroid disorders', 'Cancer',
        'Mental illness / depression', 'Stroke / heart attack', 'Parkinson',
        'Any death before age 60', 'Other',
      ] },
      { key: 'family_tendencies', label: 'General family tendencies:', type: 'multi', options: [
        'Anxiety / overthinking', 'Poor sleep / insomnia', 'Diabetes / weight gain',
        'Thyroid issues', 'Early stiffness / slowness / tremor', 'Autoimmune tendency',
        'Cancer tendency', 'Heart disease tendency', 'Digestive weakness / constipation',
        'Depression / low mood', 'None known', 'Not sure', 'Other',
      ] },
    ],
  },
  {
    key: 'uploads', title: 'File Uploads',
    fields: [
      { key: 'lab_reports', label: 'Upload lab reports (PDF / image)', type: 'file', accept: 'application/pdf,image/*' },
      { key: 'face_photo', label: 'Upload face photo', type: 'file', accept: 'image/*' },
      { key: 'audio_health_description', label: 'Upload short audio describing health concerns',
        type: 'file', accept: 'audio/*' },
    ],
  },
  {
    key: 'goals', title: 'Goals, Fears & Alignment',
    fields: [
      { key: 'top_health_goal', label: 'Top health goal in next 3 months', type: 'textarea' },
      { key: 'healing_meaning', label: 'What does "healing" mean to you?', type: 'textarea' },
      { key: 'biggest_fear', label: 'What are you afraid will happen if this is not solved?', type: 'textarea' },
      { key: 'open_to_change', label: 'Are you open to making lifestyle changes?', type: 'single',
        options: ['Yes', 'No', 'Other'] },
    ],
  },
  {
    key: 'practical', title: 'Practical & Financial Reality',
    fields: [
      { key: 'healing_pace', label: 'Ideal pace of healing', type: 'single',
        options: ['Gentle', 'Focused', 'Urgent'] },
      { key: 'financial_bandwidth', label: 'Financial bandwidth', type: 'single',
        options: ['Comfortable', 'Moderate', 'Cautious', 'Unsure'] },
      { key: 'work_stress', label: 'Work stress level', type: 'single',
        options: ['Low', 'Medium', 'High'] },
      { key: 'daily_work_hours', label: 'Average daily working hours', type: 'number' },
      { key: 'professional_context', label: 'Your professional life — anything you could share comfortably',
        type: 'textarea' },
      { key: 'home_support', label: 'Support at home for food prep / care', type: 'single',
        options: ['Yes', 'No', 'Sometimes'] },
    ],
  },
  {
    key: 'communication', title: 'Communication & Commitment',
    fields: [
      { key: 'support_mode', label: 'Preferred mode of support', type: 'single',
        options: ['Calls + PDFs', 'WhatsApp / Chat', 'Video sessions', 'Minimal updates'] },
      { key: 'daily_logging_ok', label: 'If daily 10–15 min logging is required, are you open?',
        type: 'single', options: ['Yes', 'No', 'Sometimes'] },
      { key: 'acknowledgement',
        label: 'I understand this assessment maps my biological arrival state (BAS–NAS–TAS) and is not a medical diagnosis.',
        type: 'checkbox', required: true },
    ],
  },
];

// ─── Counselor follow-up schema ────────────────────────────────────
export const COUNSELOR_FOLLOWUP_SCHEMA = [
  {
    key: 'record_linking', title: 'Record Linking',
    fields: [
      { key: 'title', label: 'Title', type: 'single', options: ['Mr.', 'Ms.', 'Mrs.', 'Dr.'] },
      { key: 'first_name', label: 'First Name', type: 'text' },
      { key: 'last_name', label: 'Last Name', type: 'text' },
      { key: 'linked_subscriber_id', label: 'Linked Subscriber ID', type: 'text' },
      { key: 'counselor_name', label: 'Counselor Name', type: 'text' },
      { key: 'followup_date', label: 'Date of Follow-Up Call / Interaction', type: 'date' },
      { key: 'followup_mode', label: 'Follow-Up Mode', type: 'multi',
        options: ['Phone Call', 'Video Call', 'In-person', 'WhatsApp Chat', 'Family Conversation', 'Mixed'] },
    ],
  },
  {
    key: 'identity_condition', title: 'Identity + Condition Clarification',
    fields: [
      { key: 'main_problem', label: 'Main problem affecting the client most right now', type: 'textarea' },
      { key: 'what_started_first', label: 'What seems to have started first?', type: 'textarea' },
      { key: 'current_burden', label: 'Current biggest burden in the present phase', type: 'textarea' },
      { key: 'cocondition', label: 'Relevant co-condition or background issue', type: 'textarea' },
      { key: 'identity_summary', label: 'Counselor one-line identity summary', type: 'textarea' },
    ],
  },
  {
    key: 'symptom_intelligence', title: 'Symptom Intelligence',
    fields: [
      { key: 'top_symptoms', label: 'Which 2-3 symptoms are most disturbing in daily life?', type: 'textarea' },
      { key: 'symptoms_together', label: 'Do any symptoms usually come together?', type: 'textarea' },
      { key: 'time_of_day', label: 'Is there a time-of-day pattern?', type: 'textarea' },
      { key: 'first_sign_bad_day', label: 'First sign of a bad day / flare / unstable phase?', type: 'textarea' },
      { key: 'variability_level', label: 'Symptom variability level', type: 'single',
        options: ['Mostly stable', 'Mildly variable', 'Clearly fluctuating', 'Highly unpredictable'] },
      { key: 'pattern_summary', label: 'Counselor pattern summary', type: 'textarea' },
    ],
  },
  {
    key: 'symptom_verification', title: 'Symptom Verification',
    fields: [
      { key: 'family_notices', label: 'What do family / caregivers notice that client may not?', type: 'textarea' },
      { key: 'side_time_trigger_worse', label: 'Is one side / one time / one trigger clearly worse?', type: 'textarea' },
      { key: 'uncertain_reports', label: 'Anything the client reports that feels uncertain?', type: 'textarea' },
      { key: 'self_report_reliability', label: 'Reliability of symptom self-report', type: 'single',
        options: ['Highly reliable', 'Mostly reliable', 'Mixed / needs family confirmation', 'Often unclear'] },
      { key: 'verification_summary', label: 'Counselor verification summary', type: 'textarea' },
    ],
  },
  {
    key: 'medication_response', title: 'Medication Response',
    fields: [
      { key: 'response_latency', label: 'How long after main medicine does response appear?', type: 'textarea' },
      { key: 'response_duration', label: 'How long does benefit usually last?', type: 'textarea' },
      { key: 'food_timing_effect', label: 'Does food, routine, or timing disturb medicine response?', type: 'textarea' },
      { key: 'wearing_off_pattern', label: 'Is there any wearing-off pattern?', type: 'textarea' },
      { key: 'peak_dose_issue', label: 'Any peak-dose issue / odd phase / heaviness / restlessness?', type: 'textarea' },
      { key: 'missed_dose_effect', label: 'What happens if timing is disturbed or dose is missed?', type: 'textarea' },
      { key: 'med_consistency', label: 'Medication response consistency', type: 'multi',
        options: ['Fairly consistent', 'Somewhat variable', 'Highly inconsistent', 'Not enough clarity', 'Not applicable'] },
      { key: 'med_summary', label: 'Counselor medication-response summary', type: 'textarea' },
    ],
  },
  {
    key: 'biological_terrain', title: 'Biological Terrain',
    fields: [
      { key: 'body_feels_overall', label: 'How does the body feel overall?', type: 'multi',
        options: ['Slow / blocked', 'Inflamed / reactive', 'Sensitive / fragile', 'Dry / depleted',
                  'Weak / low reserve', 'Unstable / fluctuating', 'Heavy / congested', 'Brittle recovery'] },
      { key: 'recurring_issue', label: 'What body issue keeps returning?', type: 'textarea' },
      { key: 'day_defining_issue', label: 'Which of these defines the day most strongly?', type: 'multi',
        options: ['Constipation / bowel delay', 'Bloating / heaviness', 'Low energy', 'Poor recovery',
                  'Heat sensitivity', 'Cold sensitivity', 'Internal restlessness', 'Inflammation / body pain',
                  'Sleep disturbance', 'No single dominant pattern'] },
      { key: 'resilience', label: 'Biological resilience feels', type: 'single',
        options: ['Strong and steady', 'Fair but disturbed easily', 'Fragile / low reserve', 'Very depleted', 'Unclear'] },
      { key: 'terrain_summary', label: 'Counselor terrain summary', type: 'textarea' },
    ],
  },
  {
    key: 'exposure_history', title: 'Exposure History',
    fields: [
      { key: 'meaningful_exposures', label: 'Likely meaningful exposure history', type: 'multi',
        options: ['Agricultural pesticide exposure', 'Chemical / solvent / paint exposure',
                  'Metal / dust / industrial exposure', 'Mold / damp environment',
                  'Repeated antibiotic history', 'Long-term medication burden',
                  'Pollution / traffic / industrial air', 'Unsafe / poor-quality water exposure',
                  'No clear exposure'] },
      { key: 'exposure_context', label: 'Exposure details / context', type: 'textarea' },
      { key: 'exposure_summary', label: 'Counselor exposure summary', type: 'textarea' },
    ],
  },
  {
    key: 'family_pattern', title: 'Family Pattern',
    fields: [
      { key: 'family_pattern', label: 'Family pattern most relevant to this case', type: 'multi',
        options: ['Metabolic / diabetes / weight gain', 'Thyroid', 'Autoimmune tendency',
                  'Constipation / digestive weakness', 'Tremor / stiffness / slowness',
                  'Anxiety / overthinking', 'Poor sleep / insomnia', 'Depression / low mood',
                  'Cardiovascular / stroke / heart disease', 'Cancer tendency', 'No clear pattern'] },
      { key: 'family_health_style', label: "What stands out in the family's health style?", type: 'textarea' },
      { key: 'family_summary', label: 'Counselor family summary', type: 'textarea' },
    ],
  },
  {
    key: 'clinical_notes', title: 'Clinical Notes / Practical Truths',
    fields: [
      { key: 'hidden_truth', label: 'What important truth is not visible in forms/reports?', type: 'textarea' },
      { key: 'adherence_difficulty', label: 'What makes following health plans difficult?', type: 'textarea' },
      { key: 'communication_style', label: 'Communication style of the client', type: 'multi',
        options: ['Clear and direct', 'Needs prompting for detail', 'Underreports symptoms',
                  'Overwhelmed easily', 'Highly anxious about health', 'Needs simple structure',
                  'Family explains better than client', 'Other'] },
      { key: 'hidden_obstacle', label: 'Any hidden practical obstacle?', type: 'textarea' },
      { key: 'clinical_summary', label: 'Counselor clinical note summary', type: 'textarea' },
    ],
  },
  {
    key: 'early_interpretation', title: 'Early Biological Interpretation',
    fields: [
      { key: 'primary_driver', label: 'What seems primary in this case?', type: 'textarea' },
      { key: 'main_amplifier', label: 'What seems to amplify the case most?', type: 'textarea' },
      { key: 'repeating_pattern', label: 'What pattern keeps repeating most clearly?', type: 'textarea' },
      { key: 'interpretation_summary', label: 'Counselor early interpretation summary', type: 'textarea' },
    ],
  },
  {
    key: 'biological_profile', title: 'Biological Profile',
    fields: [
      { key: 'body_behavior', label: 'How does the body behave by nature?', type: 'textarea' },
      { key: 'recovery_style', label: 'Recovery style', type: 'single',
        options: ['Fast recovery', 'Moderate recovery', 'Slow recovery', 'Fragile / poor recovery', 'Unclear'] },
      { key: 'bio_profile_summary', label: 'Biological profile summary', type: 'textarea' },
    ],
  },
  {
    key: 'emotional_profile', title: 'Emotional Profile',
    fields: [
      { key: 'emotional_impact', label: 'How much do stress/fear/emotion affect the body?', type: 'single',
        options: ['Minimal', 'Mild', 'Moderate', 'Strong', 'Very strong'] },
      { key: 'emotional_pattern', label: 'Emotional pattern most relevant', type: 'multi',
        options: ['Anxiety', 'Irritability', 'Shutdown / low drive', 'Emotional suppression',
                  'Mood fluctuation', 'Hypervigilance', 'Fear of worsening', 'Needs reassurance',
                  'No clear emotional amplifier', 'Other'] },
      { key: 'physical_on_emotion', label: 'Physical response when emotional load rises?', type: 'textarea' },
      { key: 'emotional_summary', label: 'Emotional profile summary', type: 'textarea' },
    ],
  },
  {
    key: 'family_personal_profile', title: 'Family & Personal Profile',
    fields: [
      { key: 'main_supporter', label: 'Who supports the client most?', type: 'textarea' },
      { key: 'home_support_level', label: 'Home support level', type: 'single',
        options: ['Strong support', 'Moderate support', 'Limited support', 'Inconsistent support', 'Mostly alone', 'Other'] },
      { key: 'too_much_responsibility', label: 'Carrying too much responsibility?', type: 'single',
        options: ['Yes, clearly', 'Somewhat', 'No', 'Not sure'] },
      { key: 'family_personal_summary', label: 'Family / personal profile summary', type: 'textarea' },
    ],
  },
  {
    key: 'environmental_profile', title: 'Environmental Profile',
    fields: [
      { key: 'environment_supports_recovery', label: 'Does environment support recovery or drain it?', type: 'single',
        options: ['Strongly supports recovery', 'Somewhat supportive', 'Neutral', 'Mildly draining', 'Clearly draining'] },
      { key: 'environment_burdens', label: 'Environment burdens present', type: 'multi',
        options: ['Noise', 'Pollution', 'Mold / damp', 'Crowding', 'Poor sleep environment',
                  'Routine disturbance', 'Travel burden', 'No major burden', 'Other'] },
      { key: 'environmental_summary', label: 'Environmental profile summary', type: 'textarea' },
    ],
  },
  {
    key: 'professional_lifestyle_profile', title: 'Professional & Lifestyle Profile',
    fields: [
      { key: 'routine_effect', label: 'Does routine support healing or make it harder?', type: 'single',
        options: ['Strongly supports healing', 'Somewhat supportive', 'Neutral', 'Makes healing harder', 'Strongly disrupts healing'] },
      { key: 'lifestyle_burden', label: 'Major lifestyle burden', type: 'multi',
        options: ['Irregular meals', 'Irregular sleep', 'Long work hours', 'Sedentary load',
                  'Excess travel', 'Stress-heavy routine', 'Low movement', 'Poor recovery time',
                  'Digital overload', 'No major burden', 'Other'] },
      { key: 'lifestyle_summary', label: 'Lifestyle / routine summary', type: 'textarea' },
    ],
  },
  {
    key: 'counselor_summary', title: 'Counselor Summary Block',
    fields: [
      { key: 'top_triggers', label: 'Top 3 repeated triggers', type: 'textarea' },
      { key: 'top_relief', label: 'Top 3 relief / modulation clues', type: 'textarea' },
      { key: 'symptom_sequence', label: 'Main symptom sequence', type: 'textarea' },
      { key: 'life_blocker', label: 'Main life blocker', type: 'textarea' },
      { key: 'terrain_reading', label: 'Main terrain reading', type: 'textarea' },
      { key: 'ready_for_entry', label: 'Ready for Biological Context entry?', type: 'single',
        options: ['Yes', 'Mostly yes, minor gaps remain', 'No, more clarification needed'] },
      { key: 'gaps_remaining', label: 'If gaps remain, what is still missing?', type: 'textarea',
        conditional: { field: 'ready_for_entry', notValue: 'Yes' } },
    ],
  },
];
